import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {Router} from "@angular/router";
import {catchError} from "rxjs/operators";
import {ToasterService} from "../services/core/toaster.service";
import {UserService} from "../services/core/user.service";
import {SKIP_FORBIDDEN_REDIRECT} from "./http-context-tokens";
import {RealtimeSessionService} from "../services/realtime/realtime-session.service";
import {DialogService} from "../services/core/dialog.service";

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {

  /**
   * Endpoints that answer 401 for a *failed attempt*, not an expired session —
   * the ERP login returns 401 on bad credentials. Signing the user out here
   * would be wrong: let the auth page show its own error.
   */
  private static readonly PUBLIC_AUTH_PATHS = [
    '/login',
    '/auth/impersonate',
    '/forgot-password',
    '/reset-password',
  ];

  /** Set once a 401 has started the sign-out, so parallel 401s don't stack up. */
  private isSigningOut = false;

  constructor(
    private router: Router,
    private toasterService: ToasterService,
    private currentUser: UserService,
    private injector: Injector,
  ) {}

  /**
   * Resolved lazily, never injected in the constructor: RealtimeSessionService
   * depends (via PermissionSyncService) on HttpClient, and HttpClient depends on
   * the interceptor chain — constructor injection would be a cyclic dependency.
   */
  private stopRealtimeSession(): void {
    try {
      this.injector.get(RealtimeSessionService).stop();
    } catch {
      // Teardown must never block the sign-out redirect.
    }
  }

  /**
   * Dialogs live in the CDK overlay container on document.body, outside the
   * router-outlet tree, so the redirect below does not destroy them — without
   * this they stay on screen over the login page. Resolved through the
   * injector for the same reason as above, and guarded so a failure here can
   * never block the sign-out redirect.
   */
  private closeOpenDialogs(): void {
    try {
      this.injector.get(DialogService).closeAll();
    } catch {
      // Teardown must never block the sign-out redirect.
    }
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: any) => {
        // 401 = the bearer token is gone, expired or revoked. Nothing on the page
        // can recover, so drop the session and send the user back to login.
        if (error instanceof HttpErrorResponse && error.status === 401) {
          const isPublicAuth = UnauthorizedInterceptor.PUBLIC_AUTH_PATHS.some(
            (path) => request.url.includes(path),
          );
          if (!isPublicAuth && !this.isSigningOut) {
            this.isSigningOut = true;
            this.closeOpenDialogs();
            this.stopRealtimeSession();
            this.currentUser.flushAll();
            this.toasterService.showErrorMessage('Your session has expired. Please sign in again.');
            this.router.navigateByUrl('/auth/login').finally(() => (this.isSigningOut = false));
          }
          return throwError(error);
        }

        if(error instanceof HttpErrorResponse && error.status == 403) {
          // Let a request opt out of the global redirect and handle its own 403
          // (e.g. document listing shows a toast and keeps the page + sidebar).
          if (!request.context.get(SKIP_FORBIDDEN_REDIRECT)) {
            this.router.navigate(['/access-denied']);
            const errorMessage = `${error.error.message}`;
            this.toasterService.showErrorMessage(errorMessage);
          }
        }
        return throwError(error);
      })
    );
  }
}
